import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Loader2, UserPlus, Trash2, Save, Shield, User as UserIcon, KeyRound } from "lucide-react";
import { toast } from "sonner";

const fmtDt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const SECTION_LABELS = {
  prezzi: "Prezzi e tariffe",
  "sconti-durata": "Sconti durata",
  testi: "Testi messaggio",
  profitometro: "Profit-o-metro",
  "prompt-ai": "Prompt AI",
  "modelli-ai": "Modelli AI",
  statistiche: "Statistiche AI",
  "self-storage": "Self-storage",
  catalogo: "Catalogo",
  "sconti-volume": "Sconti volume",
};

function SectionChecks({ sections, value, onChange, disabled, idPrefix }) {
  const toggle = (s) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
      {sections.map((s) => (
        <label key={s} className={`flex items-center gap-2 text-xs cursor-pointer ${disabled ? "opacity-50" : ""}`}>
          <input type="checkbox" checked={value.includes(s)} disabled={disabled}
            onChange={() => toggle(s)} data-testid={`${idPrefix}-sec-${s}`} />
          {SECTION_LABELS[s] || s}
        </label>
      ))}
    </div>
  );
}

export default function UserManagement({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", role: "user", allowed_sections: ["testi"], can_view_all_quotes: false });
  const [edits, setEdits] = useState({});
  const [openLog, setOpenLog] = useState(null);
  const [logs, setLogs] = useState({});

  const toggleLog = async (u) => {
    if (openLog === u.id) { setOpenLog(null); return; }
    setOpenLog(u.id);
    try {
      const { data } = await api.get(`/admin/activity?uid=${u.id}&limit=50`);
      setLogs((m) => ({ ...m, [u.id]: data.activity || [] }));
    } catch { toast.error("Impossibile caricare il registro attività"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
      setSections(data.sections);
    } catch { toast.error("Impossibile caricare gli utenti"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Utente creato");
      setForm({ email: "", password: "", role: "user", allowed_sections: ["testi"], can_view_all_quotes: false });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione utente"); }
    finally { setCreating(false); }
  };

  const setEdit = (id, patch) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), ...patch } }));

  const saveUser = async (u) => {
    const e = edits[u.id] || {};
    const body = {
      role: e.role ?? u.role,
      allowed_sections: e.allowed_sections ?? u.allowed_sections,
      can_view_all_quotes: e.can_view_all_quotes ?? u.can_view_all_quotes,
    };
    if (e.password) body.password = e.password;
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      toast.success("Utente aggiornato");
      setEdits((prev) => { const c = { ...prev }; delete c[u.id]; return c; });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore aggiornamento"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente eliminato");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore eliminazione"); }
  };

  const toggleDisabled = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Accesso riabilitato" : "Accesso disabilitato");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore aggiornamento accesso"); }
  };

  return (
    <section id="sec-gestione-utenti" className="scroll-mt-36" data-testid="user-management">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="h-5 w-5 text-[#1a5f5a]" />
        <h3 className="font-display font-semibold text-lg">Gestione utenti</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">Crea e gestisci gli operatori. Gli <b>admin</b> hanno accesso a tutte le sezioni; gli utenti <b>user</b> vedono solo le sezioni di Impostazioni che abiliti (di default solo "Testi messaggio"). Tutti possono comunque creare preventivi e usare l'app.</p>

      {/* Create */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-5">
        <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Nuovo utente</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@stormi.it" className="mt-1" data-testid="new-user-email" /></div>
          <div><Label className="text-xs">Password iniziale</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 caratteri" className="mt-1" data-testid="new-user-password" /></div>
          <div>
            <Label className="text-xs">Ruolo</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user" data-testid="new-user-role-user">User</SelectItem>
                <SelectItem value="admin" data-testid="new-user-role-admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end"><Button onClick={create} disabled={creating} className="w-full bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="new-user-create">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Crea</Button></div>
        </div>
        {form.role === "user" && (
          <div className="mt-3">
            <Label className="text-xs">Sezioni Impostazioni consentite</Label>
            <div className="mt-1.5"><SectionChecks sections={sections} value={form.allowed_sections} onChange={(v) => setForm({ ...form, allowed_sections: v })} idPrefix="new-user" /></div>
            <label className="flex items-center gap-2 text-xs cursor-pointer mt-3">
              <input type="checkbox" checked={form.can_view_all_quotes} onChange={(e) => setForm({ ...form, can_view_all_quotes: e.target.checked })} data-testid="new-user-viewall" />
              Può vedere <b>tutti i preventivi</b> (non solo i propri)
            </label>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? <div className="py-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
        <div className="space-y-3">
          {users.map((u) => {
            const e = edits[u.id] || {};
            const role = e.role ?? u.role;
            const allowed = e.allowed_sections ?? u.allowed_sections;
            const viewAll = e.can_view_all_quotes ?? u.can_view_all_quotes;
            const isSelf = u.id === currentUserId;
            return (
              <div key={u.id} className={`border rounded-md p-4 ${u.disabled ? "border-red-200 bg-red-50/40" : "border-slate-200"}`} data-testid={`user-row-${u.email}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    {role === "admin" ? <Shield className="h-4 w-4 text-[#1a5f5a]" /> : <UserIcon className="h-4 w-4 text-slate-400" />}
                    <span className="font-medium text-sm text-slate-800 truncate">{u.email}{isSelf && <span className="text-[11px] text-slate-400"> (tu)</span>}</span>
                    {u.disabled && <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600 bg-red-100 rounded px-1.5 py-0.5" data-testid={`user-disabled-badge-${u.email}`}>Disabilitato</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs mr-1 cursor-pointer" title="Abilita o disabilita l'accesso senza eliminare l'utente">
                      <Switch checked={!u.disabled} disabled={isSelf} onCheckedChange={() => toggleDisabled(u)} data-testid={`user-access-toggle-${u.email}`} />
                      <span className={u.disabled ? "text-red-500 font-medium" : "text-emerald-600"}>{u.disabled ? "Off" : "Attivo"}</span>
                    </label>
                    <Select value={role} onValueChange={(v) => setEdit(u.id, { role: v })}>
                      <SelectTrigger className="h-8 w-28" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => saveUser(u)} className="gap-1.5 h-8" data-testid={`user-save-${u.email}`}><Save className="h-3.5 w-3.5" /> Salva</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" disabled={isSelf} onClick={() => removeUser(u)} data-testid={`user-delete-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-slate-400" data-testid={`user-lastlogin-${u.email}`}>
                  Ultimo accesso: <span className="text-slate-500">{u.last_login_at ? fmtDt(u.last_login_at) : "mai"}</span>
                </div>
                {role === "user" && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <Label className="text-[11px] text-slate-500">Sezioni consentite</Label>
                    <div className="mt-1.5"><SectionChecks sections={sections} value={allowed} onChange={(v) => setEdit(u.id, { allowed_sections: v })} idPrefix={`user-${u.email}`} /></div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer mt-3">
                      <input type="checkbox" checked={!!viewAll} onChange={(ev) => setEdit(u.id, { can_view_all_quotes: ev.target.checked })} data-testid={`user-viewall-${u.email}`} />
                      Può vedere <b>tutti i preventivi</b> (non solo i propri)
                    </label>
                  </div>
                )}
                {role === "admin" && (
                  <div className="mt-2 text-[11px] text-slate-400">Vede tutti i preventivi (admin)</div>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <KeyRound className="h-3.5 w-3.5 text-slate-400" />
                  <Input value={e.password || ""} onChange={(ev) => setEdit(u.id, { password: ev.target.value })} placeholder="Nuova password (opzionale)" className="h-8 text-sm max-w-xs" data-testid={`user-password-${u.email}`} />
                </div>
                <div className="mt-2">
                  <button type="button" onClick={() => toggleLog(u)} className="text-xs text-[#1a5f5a] hover:underline" data-testid={`user-activity-toggle-${u.email}`}>
                    {openLog === u.id ? "Nascondi registro attività" : "Mostra registro attività"}
                  </button>
                  {openLog === u.id && (
                    <div className="mt-2 border border-slate-100 rounded max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid={`user-activity-${u.email}`}>
                      {(logs[u.id] || []).length === 0 ? (
                        <div className="px-2 py-2 text-xs text-slate-400">Nessuna attività recente.</div>
                      ) : (logs[u.id] || []).map((r) => (
                        <div key={r.id} className="flex items-center justify-between px-2 py-1.5 text-xs">
                          <span className="text-slate-700 truncate">{r.action}{r.detail ? ` — ${r.detail}` : ""}</span>
                          <span className="text-slate-400 whitespace-nowrap ml-2">{fmtDt(r.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
